import React, { useState, useEffect, useRef } from 'react';
import { 
  BugAntIcon, 
  LightBulbIcon,
  ExclamationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import FileAttachment from './FileAttachment';

/**
 * FeedbackForm component for collecting user feedback with validation
 * Supports both bug reports and improvement suggestions
 */
const FeedbackForm = ({ 
  onSubmit, 
  isSubmitting = false, 
  error = null
}) => {
  const [formData, setFormData] = useState({
    type: null,
    title: '',
    description: '',
    attachments: []
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);

  const titleInputRef = useRef(null);
  const descriptionInputRef = useRef(null);

  // Validation rules
  const validationRules = {
    type: {
      required: true,
      message: 'Please select a feedback type'
    },
    title: {
      required: true,
      minLength: 5,
      maxLength: 100,
      message: 'Title must be between 5 and 100 characters'
    },
    description: {
      required: false, // Description is now optional
      minLength: 10,
      maxLength: 1000,
      message: 'Description must be between 10 and 1000 characters'
    },
    attachments: {
      required: false,
      message: 'Invalid attachments'
    }
  };

  // Validate individual field
  const validateField = (name, value) => {
    const rules = validationRules[name];
    if (!rules) return null;

    const trimmedValue = value ? value.toString().trim() : '';
    const isEmpty = trimmedValue === '';

    // If field is required and empty, return error
    if (rules.required && isEmpty) {
      return rules.message || `${name} is required`;
    }

    // If field is optional and empty, it's valid
    if (!rules.required && isEmpty) {
      return null;
    }

    // If field has content, validate length constraints
    if (!isEmpty) {
      if (rules.minLength && trimmedValue.length < rules.minLength) {
        return rules.message || `${name} must be at least ${rules.minLength} characters`;
      }

      if (rules.maxLength && trimmedValue.length > rules.maxLength) {
        return rules.message || `${name} must be no more than ${rules.maxLength} characters`;
      }
    }

    return null;
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    let formIsValid = true;

    Object.keys(validationRules).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        formIsValid = false;
      }
    });

    setErrors(newErrors);
    setIsValid(formIsValid);
    return formIsValid;
  };

  // Handle field changes with real-time validation
  const handleFieldChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate field if it's been touched
    if (touched[name] || value !== '') {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      type: true,
      title: true,
      description: true,
      attachments: true
    });

    // Validate form
    if (validateForm()) {
      onSubmit(formData);
    } else {
      // Focus on first error field
      if (errors.type) {
        // Focus on type selection area
        document.querySelector('[data-feedback-type]')?.focus();
      } else if (errors.title) {
        titleInputRef.current?.focus();
      } else if (errors.description) {
        descriptionInputRef.current?.focus();
      }
    }
  };

  // Update form validity when formData changes
  useEffect(() => {
    const timer = setTimeout(() => {
      validateForm();
    }, 300); // Debounce validation

    return () => clearTimeout(timer);
  }, [formData]);

  // Focus on title input when type is selected
  useEffect(() => {
    if (formData.type && !formData.title) {
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [formData.type]);

  const getCharacterCount = (field) => {
    const value = formData[field] || '';
    const maxLength = validationRules[field]?.maxLength || 0;
    return `${value.length}/${maxLength}`;
  };

  const getCharacterCountColor = (field) => {
    const value = formData[field] || '';
    const maxLength = validationRules[field]?.maxLength || 0;
    const percentage = value.length / maxLength;
    
    if (percentage >= 0.9) return 'text-red-600 dark:text-red-400';
    if (percentage >= 0.8) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-gray-500 dark:text-gray-400';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Feedback Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
          Feedback Type
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {/* Bug Report Option */}
          <button
            type="button"
            data-feedback-type="bug"
            onClick={() => handleFieldChange('type', 'bug')}
            className={`relative p-3 rounded-lg border-2 transition-all duration-200 text-left
              ${formData.type === 'bug'
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20 ring-2 ring-red-500 ring-opacity-20'
                : 'border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-600 bg-white dark:bg-gray-800'
              }
              ${touched.type && errors.type ? 'border-red-500' : ''}
            `}
          >
            <div className="flex items-center gap-3">
              <BugAntIcon className={`w-5 h-5 flex-shrink-0 ${
                formData.type === 'bug' 
                  ? 'text-red-600 dark:text-red-400' 
                  : 'text-gray-400 dark:text-gray-500'
              }`} />
              <div>
                <h3 className={`text-sm font-medium ${
                  formData.type === 'bug'
                    ? 'text-red-900 dark:text-red-100'
                    : 'text-gray-900 dark:text-white'
                }`}>
                  Bug Report
                </h3>
                <p className={`text-xs ${
                  formData.type === 'bug'
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  Something isn't working
                </p>
              </div>
            </div>
            {formData.type === 'bug' && (
              <CheckCircleIcon className="absolute top-2 right-2 w-4 h-4 text-red-600 dark:text-red-400" />
            )}
          </button>

          {/* Improvement Suggestion Option */}
          <button
            type="button"
            data-feedback-type="improvement"
            onClick={() => handleFieldChange('type', 'improvement')}
            className={`relative p-3 rounded-lg border-2 transition-all duration-200 text-left
              ${formData.type === 'improvement'
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 ring-2 ring-purple-500 ring-opacity-20'
                : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 bg-white dark:bg-gray-800'
              }
              ${touched.type && errors.type ? 'border-red-500' : ''}
            `}
          >
            <div className="flex items-center gap-3">
              <LightBulbIcon className={`w-5 h-5 flex-shrink-0 ${
                formData.type === 'improvement' 
                  ? 'text-purple-600 dark:text-purple-400' 
                  : 'text-gray-400 dark:text-gray-500'
              }`} />
              <div>
                <h3 className={`text-sm font-medium ${
                  formData.type === 'improvement'
                    ? 'text-purple-900 dark:text-purple-100'
                    : 'text-gray-900 dark:text-white'
                }`}>
                  Improvement Suggestion
                </h3>
                <p className={`text-xs ${
                  formData.type === 'improvement'
                    ? 'text-purple-700 dark:text-purple-300'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  Ideas to make it better
                </p>
              </div>
            </div>
            {formData.type === 'improvement' && (
              <CheckCircleIcon className="absolute top-2 right-2 w-4 h-4 text-purple-600 dark:text-purple-400" />
            )}
          </button>
        </div>
        
        {touched.type && errors.type && (
          <div className="mt-2 flex items-center gap-2 text-red-600 dark:text-red-400">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{errors.type}</span>
          </div>
        )}
      </div>

      {/* Title Field */}
      <div>
        <label htmlFor="feedback-title" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
          Title
        </label>
        <div className="relative">
          <input
            ref={titleInputRef}
            id="feedback-title"
            type="text"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            placeholder={formData.type === 'bug' ? 'Briefly describe the bug...' : 'Briefly describe your suggestion...'}
            className={`w-full px-4 py-3 rounded-lg border transition-colors
              ${touched.title && errors.title
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:border-purple-500 focus:ring-purple-500'
              }
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white
              placeholder-gray-500 dark:placeholder-gray-400
              focus:outline-none focus:ring-2 focus:ring-opacity-20
            `}
            maxLength={validationRules.title.maxLength}
          />
          <div className={`absolute right-3 top-3 text-xs ${getCharacterCountColor('title')}`}>
            {getCharacterCount('title')}
          </div>
        </div>
        
        {touched.title && errors.title && (
          <div className="mt-2 flex items-center gap-2 text-red-600 dark:text-red-400">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{errors.title}</span>
          </div>
        )}
      </div>

      {/* Description Field */}
      <div>
        <label htmlFor="feedback-description" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
          Description <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
        </label>
        <div className="relative">
          <textarea
            ref={descriptionInputRef}
            id="feedback-description"
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder={
              formData.type === 'bug' 
                ? 'Please provide detailed steps to reproduce the bug, what you expected to happen, and what actually happened...'
                : 'Please describe your suggestion in detail. What would you like to see improved and how would it help you?...'
            }
            rows={4}
            className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none
              ${touched.description && errors.description
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:border-purple-500 focus:ring-purple-500'
              }
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white
              placeholder-gray-500 dark:placeholder-gray-400
              focus:outline-none focus:ring-2 focus:ring-opacity-20
            `}
            maxLength={validationRules.description.maxLength}
          />
          <div className={`absolute right-3 bottom-3 text-xs ${getCharacterCountColor('description')}`}>
            {getCharacterCount('description')}
          </div>
        </div>
        
        {touched.description && errors.description && (
          <div className="mt-2 flex items-center gap-2 text-red-600 dark:text-red-400">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{errors.description}</span>
          </div>
        )}
      </div>

      {/* File Attachments */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
          Attachments <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
        </label>
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
          Add screenshots, logs, or files to help explain your {formData.type === 'bug' ? 'bug' : 'suggestion'}
        </p>
        
        <FileAttachment
          files={formData.attachments}
          onChange={(files) => handleFieldChange('attachments', files)}
          error={touched.attachments && errors.attachments ? errors.attachments : null}
          disabled={isSubmitting}
        />
      </div>

      {/* Global Error Message */}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="flex items-start gap-2 text-red-800 dark:text-red-200">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-sm font-medium">Error submitting feedback</span>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form Actions - Sticky Submit Button for Mobile */}
      <div className="pb-20 sm:pb-2">
        {/* Desktop submit button */}
        <div className="hidden sm:block pt-2">
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200
              ${isValid && !isSubmitting
                ? 'bg-purple-600 hover:bg-purple-700 text-white hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                {formData.type === 'bug' ? (
                  <BugAntIcon className="w-4 h-4" />
                ) : (
                  <LightBulbIcon className="w-4 h-4" />
                )}
                <span>Submit {formData.type === 'bug' ? 'Bug Report' : 'Suggestion'}</span>
              </>
            )}
          </button>
        </div>

        {/* Mobile sticky submit button */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 shadow-lg">
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-medium transition-all duration-200
              ${isValid && !isSubmitting
                ? 'bg-purple-600 hover:bg-purple-700 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                {formData.type === 'bug' ? (
                  <BugAntIcon className="w-4 h-4" />
                ) : (
                  <LightBulbIcon className="w-4 h-4" />
                )}
                <span>Submit {formData.type === 'bug' ? 'Bug Report' : 'Suggestion'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default FeedbackForm;